import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import useApi from '../../../utils/useApi';
import UIImageSuggestionModal from './UIImageSuggestionModal';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  headerBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  suggestionButton: {
    color: '#17497F',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 6,
    fontSize: '0.8rem',
    marginTop: theme.spacing(1),
    '&:hover': {
      backgroundColor: 'rgba(23, 73, 127, 0.08)',
    },
  },
  uploadButton: {
    border: '2px dashed #17497F',
    backgroundColor: '#F4F7FC',
    color: '#17497F',
    padding: theme.spacing(2),
    textAlign: 'center',
    cursor: 'pointer',
    borderRadius: 8,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#EAEFF8',
      borderColor: '#0D2D52',
    },
  },
  previewCard: {
    position: 'relative',
    width: 100,
    height: 100,
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #E0E0E0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  deleteButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: '#fff',
    padding: 4,
    '&:hover': {
      backgroundColor: 'rgba(211, 47, 47, 0.9)',
    },
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
}));

const formatImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

export default function UIImageUpload({
  id,
  images,
  value,
  onChange,
  maxImages = 5,
  label = 'Imagens do Item',
  helperText,
  itemName = '',
  hideSuggestions = false,
  folder = '',
}) {
  const classes = useStyles();
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openSuggestionModal, setOpenSuggestionModal] = useState(false);

  const inputId = useMemo(
    () => id || `ui-image-upload-input-${Math.random().toString(36).substring(2, 9)}`,
    [id]
  );

  // Normalize image list from images or value prop
  const rawList = images !== undefined ? images : value;
  const imageList = Array.isArray(rawList)
    ? rawList.filter(Boolean)
    : typeof rawList === 'string' && rawList.trim() !== ''
    ? [rawList.trim()]
    : [];

  const isSingleMode = maxImages === 1;

  const [uploadApi] = useApi({
    method: 'post',
    url: '/storage/upload',
  });

  const notifyChange = (updatedList) => {
    if (!onChange) return;
    if (isSingleMode) {
      onChange(updatedList[0] || '');
    } else {
      onChange(updatedList);
    }
  };

  const handleFileChange = async (event) => {
    const targetInput = event.target;
    const selectedFiles = Array.from(targetInput?.files || []);
    if (selectedFiles.length === 0) return;

    const validFiles = selectedFiles.filter((file) => file.type.startsWith('image/'));
    if (validFiles.length === 0) {
      setErrorMessage('Por favor, selecione arquivos de imagem válidos.');
      if (targetInput) targetInput.value = '';
      return;
    }

    const availableSlots = maxImages - imageList.length;
    if (availableSlots <= 0) {
      setErrorMessage(`Limite máximo de ${maxImages} imagem(ns) atingido.`);
      if (targetInput) targetInput.value = '';
      return;
    }

    const filesToUpload = validFiles.slice(0, availableSlots);
    let limitNotice = '';
    if (validFiles.length > availableSlots) {
      limitNotice = `Apenas as primeiras ${availableSlots} imagens foram selecionadas para respeitar o limite de ${maxImages}.`;
    }

    setErrorMessage(limitNotice);
    setUploading(true);

    const newUploadedUrls = [];

    for (const file of filesToUpload) {
      const formData = new FormData();
      formData.append('file', file);
      if (folder) {
        formData.append('folder', folder);
      }

      try {
        const res = await uploadApi({ data: formData });
        if (res && res.data && res.data.url) {
          const formattedUrl = formatImageUrl(res.data.url);
          newUploadedUrls.push(formattedUrl);
        }
      } catch (err) {
        console.error('Erro no upload da imagem:', err);
      }
    }

    setUploading(false);

    if (targetInput) {
      try {
        targetInput.value = '';
      } catch (e) {
        // Input unmounted
      }
    }

    if (newUploadedUrls.length > 0) {
      const updated = [...imageList, ...newUploadedUrls];
      notifyChange(updated);
    } else if (!limitNotice) {
      setErrorMessage('Erro ao realizar o upload das imagens.');
    }
  };

  const handleDelete = (index) => {
    const updated = imageList.filter((_, i) => i !== index);
    notifyChange(updated);
  };

  const handleAddSuggestedImages = (newUrls) => {
    if (!newUrls || newUrls.length === 0) return;
    const availableSlots = maxImages - imageList.length;
    const urlsToAdd = newUrls.slice(0, availableSlots);
    const updated = [...imageList, ...urlsToAdd];
    notifyChange(updated);
  };

  return (
    <Box className={classes.container}>
      {label && (
        <Box className={classes.headerBox}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#333' }}>
            {label} ({imageList.length}/{maxImages})
          </Typography>
        </Box>
      )}
      <Typography variant="caption" style={{ color: '#64748b', display: 'block', marginBottom: 10 }}>
        {helperText || 'Tamanho recomendado: 800x800 px (quadrada 1:1) em JPG ou PNG. Máx. 5MB.'}
      </Typography>

      <Grid container spacing={2} alignItems="center">
        {imageList.map((url, idx) => {
          const src = formatImageUrl(url);
          return (
            <Grid item key={idx}>
              <Box className={classes.previewCard}>
                <img
                  src={src}
                  alt={`Imagem ${idx + 1}`}
                  className={classes.previewImage}
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://images.unsplash.com/photo-1560393464-5c69a73c5770?w=300&auto=format&fit=crop&q=60';
                  }}
                />
                <IconButton
                  size="small"
                  className={classes.deleteButton}
                  onClick={() => handleDelete(idx)}
                  title="Remover imagem"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          );
        })}

        {imageList.length < maxImages && (
          <Grid item>
            <input
              accept="image/*"
              multiple={!isSingleMode}
              style={{ display: 'none' }}
              id={inputId}
              type="file"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <label htmlFor={inputId}>
              <Box className={classes.uploadButton} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                {uploading ? (
                  <CircularProgress size={28} style={{ color: '#17497F' }} />
                ) : (
                  <>
                    <CloudUploadIcon fontSize="large" />
                    <Typography variant="caption" style={{ marginTop: 4, fontWeight: 500 }}>
                      Adicionar
                    </Typography>
                  </>
                )}
              </Box>
            </label>
          </Grid>
        )}
      </Grid>

      {!hideSuggestions && imageList.length < maxImages && (
        <Button
          size="small"
          className={classes.suggestionButton}
          startIcon={<PhotoLibraryIcon fontSize="small" />}
          onClick={() => setOpenSuggestionModal(true)}
        >
          Sugerir Imagens
        </Button>
      )}

      {errorMessage && (
        <Typography variant="caption" className={classes.errorText}>
          {errorMessage}
        </Typography>
      )}

      {!hideSuggestions && (
        <UIImageSuggestionModal
          open={openSuggestionModal}
          onClose={() => setOpenSuggestionModal(false)}
          initialQuery={itemName}
          maxSelectable={maxImages - imageList.length}
          onSelectImages={handleAddSuggestedImages}
        />
      )}
    </Box>
  );
}
